import { useSeoMeta, useHead } from '@unhead/react';
import {Shield, Leaf, Truck, Users, Heart, Award, Clock3, CheckCircle2} from 'lucide-react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import aboutBg from '../../images/VIP House Movers Team Carrying Items.jpg';
import aboutImg from '../../images/VIP House Movers Team with happy customer.jpg';
import aboutImg1 from '../../images/VIP House Movers Team Packing.jpg';
import aboutImg2 from '../../images/VIP House Movers Team Packing.jpg';
import aboutImg3 from '../../images/VIP House Movers Team Loading Removal Van.jpg';
import aboutImg4 from '../../images/vip-house-movers-outdoor-removals-prep.jpeg';

export default function About() {
  useSeoMeta({
    title: 'About VIP House Movers | Trusted Wolverhampton Removals Company',
    description: 'Learn about VIP House Movers — Wolverhampton\'s trusted, insured house removal company. Professional home removals, man and van service, packing and storage across the West Midlands.',
    ogTitle: 'About VIP House Movers | Wolverhampton Removals',
    ogDescription: 'Wolverhampton\'s trusted, eco-friendly house removal company. Fully insured, 5-star rated, serving the West Midlands & UK.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/about',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'About VIP House Movers | Wolverhampton Removals',
    twitterDescription: 'Wolverhampton\'s trusted, eco-friendly house removal company.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/about');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'about VIP House Movers, Wolverhampton removals company, trusted removal company, eco-friendly movers, insured house removals, West Midlands removals, removal company story, premium removals team',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'About',
              item: 'https://www.viphousemovers.com/about',
            },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'VIP House Movers',
          url: 'https://www.viphousemovers.com',
          telephone: '+44-121-752-7107',
          email: 'info@viphousemovers.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Wolverhampton',
            addressRegion: 'West Midlands',
            addressCountry: 'GB',
          },
          sameAs: [
            'https://www.facebook.com/viphousemovers',
            'https://www.instagram.com/viphousemovers',
          ],
          description: 'Eco-friendly, fully insured house removal services in Wolverhampton and the West Midlands.',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '150',
          },
        }),
      },
    ],
  });
  return (
    <SiteShell activePath="/about" fixedNav>
      <PageHero
        eyebrow="About VIP House Movers"
        title="Your trusted, eco-friendly removal experts"
        description="Discover the story, values, and professional team behind Wolverhampton's premium VIP House Movers service."
        align="center"
        backgroundImage={aboutBg}
      />

      {/* Our Story */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/10 rounded-3xl rotate-3"></div>
              <img
                src={aboutImg}
                alt="VIP House Movers founder and team outside a branded Luton removal van in Wolverhampton, West Midlands"
                width={1200}
                height={900}
                loading="lazy"
                decoding="async"
                className="relative rounded-2xl shadow-2xl object-cover h-[500px] w-full"
              />
              <div className="absolute -bottom-8 -right-8 bg-gray-900 text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <div className="text-4xl font-bold text-[#D4AF37] mb-1">10+</div>
                <div className="font-medium text-gray-300">Years Serving<br/>the Midlands</div>
              </div>
            </div>
            <div>
              <h2 className="text-[#D4AF37] font-semibold tracking-wider uppercase mb-3">Our Story</h2>
              <h3 className="text-4xl font-bold mb-6 text-gray-900 tracking-tight">Premium removals with a personal touch</h3>
              <p className="text-gray-600 mb-4 leading-relaxed text-lg">
                Founded with the vision of making moving a stress-free experience, VIP House Movers has grown into one of Wolverhampton's most trusted removal companies. We believe that every customer deserves the "VIP" treatment, no matter the size of the move.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                Our team consists of highly trained professionals who handle your belongings with the utmost care, ensuring everything arrives safely at your new home across the West Midlands, Staffordshire, and nationwide across the UK.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="bg-gray-50 py-20 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-[#D4AF37] font-semibold tracking-wider uppercase mb-3">What Drives Us</h2>
            <h3 className="text-4xl font-bold text-gray-900 tracking-tight">Our core values</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-8 h-8 text-[#D4AF37]" />, title: 'Fully Insured', desc: 'Comprehensive goods in transit and public liability cover on every move.' },
              { icon: <Leaf className="w-8 h-8 text-green-600" />, title: 'Eco-Friendly', desc: 'Recyclable materials and fuel-efficient routes across the Midlands.' },
              { icon: <Truck className="w-8 h-8 text-[#D4AF37]" />, title: 'Modern Fleet', desc: 'Clean, maintained Luton vans and larger removal vehicles.' },
              { icon: <Users className="w-8 h-8 text-[#D4AF37]" />, title: 'Expert Team', desc: 'Uniformed, DBS-aware, and professionally trained movers.' },
            ].map((v) => (
              <div key={v.title} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center hover:border-[#D4AF37]/30 transition-colors">
                <div className="flex justify-center mb-4">{v.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team in Action */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-[#D4AF37] font-semibold tracking-wider uppercase mb-3">Our Team in Action</h2>
            <h3 className="text-4xl font-bold text-gray-900 tracking-tight">Careful, professional, and on time</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { src: aboutImg1, alt: 'VIP House Movers professional packer labelling moving boxes in a Wolverhampton home' },
              { src: aboutImg2, alt: 'VIP House Movers careful packing of fragile glassware using eco-friendly bubble wrap' },
              { src: aboutImg3, alt: 'VIP House Movers loading a Luton removal van for a UK house removal in the West Midlands' },
              { src: aboutImg4, alt: 'VIP House Movers preparing outdoor furniture and equipment for a residential garden move' },
            ].map((img) => (
              <figure key={img.src} className="relative overflow-hidden rounded-2xl shadow-lg group">
                <img
                  src={img.src}
                  alt={img.alt}
                  width={900}
                  height={700}
                  loading="lazy"
                  decoding="async"
                  className="h-[340px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-sm text-white">
                  {img.alt}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-[#D4AF37] font-semibold tracking-wider uppercase mb-3">Why homeowners trust VIP House Movers</h2>
            <h3 className="text-4xl font-bold text-gray-900 tracking-tight">Built for the kind of move customers actually search for</h3>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              Homeowners and tenants searching for a removals company want more than a van. They want insured movers, clear pricing, punctual arrivals, respectful staff, and a company that can handle packing, lifting, fragile items, and local area logistics without drama.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: 'Trusted local removals company',
                desc: 'VIP House Movers combines local Wolverhampton knowledge with a professional, premium service standard that works across the wider West Midlands and UK.',
              },
              {
                title: 'Search-led customer content',
                desc: 'We answer the topics people genuinely search for before booking: removal costs, how far in advance to book, what packing services include, and how moving day is managed.',
              },
              {
                title: 'Conversion-focused trust signals',
                desc: 'From testimonials and FAQs to service detail pages and area pages, every part of the website builds trust while strengthening topical authority for home removals SEO.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                <h4 className="text-xl font-bold text-gray-900">{item.title}</h4>
                <p className="mt-4 leading-relaxed text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose Stat strip */}
      <section className="bg-gray-900 text-white py-16 border-t-8 border-[#D4AF37]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: <Heart className="w-8 h-8 text-[#D4AF37]" />, n: '5-Star', l: 'Customer Rated' },
              { icon: <Award className="w-8 h-8 text-[#D4AF37]" />, n: '100+', l: 'Moves Each Month' },
              { icon: <Clock3 className="w-8 h-8 text-[#D4AF37]" />, n: 'On Time', l: 'Every Move' },
              { icon: <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />, n: 'Zero', l: 'Hidden Fees' },
            ].map((s) => (
              <div key={s.l} className="flex flex-col items-center gap-2">
                <div className="mb-2">{s.icon}</div>
                <div className="text-3xl font-bold text-[#D4AF37]">{s.n}</div>
                <div className="text-gray-300 text-sm">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

